import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Hourglass, MessageSquare, Sparkles, Store } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useDevAuth } from "@/context/DevAuthContext";
import { PageHero } from "@/components/layout/PageHero";
import { BillingCard } from "@/components/dev/BillingCard";
import { RoleSwitch } from "@/components/dev/RoleSwitch";
import { GroupCard } from "@/components/dev/GroupCard";
import { BusinessPlansCard } from "@/components/dev/BusinessPlansCard";
import { BusinessPrepaid } from "@/components/market/Prepaid";
import { BusinessCart } from "@/components/market/BusinessCart";
import { EmployeesCard } from "@/components/dev/EmployeesCard";
import { AntiCheatNote, CustomPlanCta } from "@/components/apps/PlanNotes";

const TrialBanner = ({ billing }) => {
  const { t, fmtDate } = useI18n();
  // A former VIP (or lapsed subscriber) whose grace ended: same « S'abonner » call to action, without trial wording.
  const tr = ["trialing", "trial_expired"].includes(billing?.trial?.status) ? billing.trial
    : billing?.status === "suspended" && !billing.by_admin ? { status: "expired" } : null;
  if (!tr) return null;
  const running = tr.status === "trialing";
  const ended = tr.status === "expired" ? "access" : "trial";
  const offer = billing.custom_offer?.flex && billing.custom_offer.state === "current";
  return (
    <div className={`flex min-w-0 flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:justify-between ${running ? "border-emerald-500/60 bg-emerald-950/40" : "border-amber-400/60 bg-amber-950/50"}`} role="status" data-testid="client-trial-banner">
      <div className="min-w-0">
        <p className="flex items-center gap-2 break-words font-display text-sm font-bold uppercase tracking-wide text-white"><Hourglass className="h-4 w-4 shrink-0" />
          <span data-testid="client-trial-title">{running ? t("trial.banner", { n: tr.days_left }) : t(`${ended}.bannerEnded`)}</span></p>
        <p className="mt-1 break-words text-xs text-neutral-300">{running ? t("trial.bannerHint", { date: fmtDate(tr.until) }) : t(`${ended}.bannerEndedHint`)}</p>
      </div>
      {offer ? (
        <a href="#billing" className="btn-gold btn-sm btn-cta shrink-0" data-testid="client-trial-subscribe">{t("trial.subscribe")}</a>
      ) : (
        <Link to="/espace-developpeur/messages?nouveau=custom" className="btn-gold btn-sm btn-cta shrink-0" data-testid="client-trial-subscribe">{t("trial.subscribe")}</Link>
      )}
    </div>
  );
};

// « Client mini-site » dashboard: subscription status, offer card, messaging, unsubscribe (no sandbox / APK hosting).
export const ClientDashboard = () => {
  const { t } = useI18n();
  const { dev, refresh } = useDevAuth();
  const [billing, setBilling] = useState(dev.billing);
  const [params] = useSearchParams();
  useEffect(() => {
    api.get("/dev/billing").then(({ data }) => setBilling(data)).catch(() => {});
  }, []);
  useEffect(() => {  // deep link (?app=&ref=&panier=1): bring the cart into view
    if (params.get("panier") || params.get("ref")) setTimeout(() => document.getElementById("panier")?.scrollIntoView({ behavior: "smooth" }), 400);
  }, [params]);
  return (
    <div data-testid="client-dashboard">
      <PageHero eyebrow={t("client.eyebrow")} title={t("dev.welcome", { name: dev.brand })}>
        <p className="mt-3 max-w-2xl break-words text-sm text-neutral-300">{t("client.lead")}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          {billing?.status !== "vip" && <Link to="/espace-developpeur/messages?nouveau=custom" className="btn-gold btn-sm btn-cta max-w-full whitespace-normal text-start" data-testid="client-request-plan-btn">
            <Sparkles className="h-4 w-4 shrink-0" /> {t("client.requestPlan")}
          </Link>}
          <Link to="/espace-developpeur/messages" className="btn-red btn-sm btn-cta" data-testid="client-messages-btn"><MessageSquare className="h-4 w-4" /> {t("msg.title")}</Link>
          <Link to="/" className="btn-red btn-sm btn-cta" data-testid="client-minisites-link"><Store className="h-4 w-4" /> {t("mini.title")}</Link>
          <RoleSwitch />
        </div>
      </PageHero>
      <div className="mx-auto max-w-3xl space-y-6 px-4 pb-16 sm:px-8">
        <TrialBanner billing={billing} />
        <GroupCard billing={billing} />
        <EmployeesCard />
        <BusinessCart app={params.get("app")} preRef={params.get("ref")} openAll={params.get("panier") === "1"} billing={billing} />
        <BusinessPlansCard />
        <BusinessPrepaid billing={billing} />
        <AntiCheatNote testId="client-anticheat" />
        <CustomPlanCta testId="client-custom-plan" />
        <div id="billing"><BillingCard billing={billing} onChange={(b) => { setBilling(b); refresh(); }} /></div>
      </div>
    </div>
  );
};
